import './assets/css/pages/history.scss'

/**
 * History Timeline
 *
 * - Pill / select navigation that scrolls to era anchors with the right offset
 *   for the sticky header + sticky pill bar.
 * - IntersectionObserver-based scroll spy that keeps the active pill in sync
 *   with the era currently in view.
 * - Reveals each timeline item (dot, year, text, image) on scroll using a
 *   second IntersectionObserver — replaces J&R's AOS dependency.
 */

const HEADER_FALLBACK = 80

/**
 * Resolve the static site header element. The locator + history pages share
 * the same measurement strategy, so the selector list here mirrors the one
 * in locations-locator.ts.
 */
function getSiteHeader(): HTMLElement | null {
  return (
    document.querySelector<HTMLElement>('header.main') ??
    document.querySelector<HTMLElement>('header[role="banner"]') ??
    document.querySelector<HTMLElement>('body > header:not(.secondary)')
  )
}

/**
 * Measure the static site header and expose it as --site-header-height so
 * the hero can pull itself up behind it (full-viewport background) and the
 * scroll-spy offset stays accurate.
 */
function syncHeaderOffset(): void {
  const header = getSiteHeader()
  if (!header) return
  const h = header.offsetHeight
  if (h > 0) {
    document.documentElement.style.setProperty('--site-header-height', `${h}px`)
  }
}

function getStickyOffset(): number {
  const headerH = getSiteHeader()?.offsetHeight ?? HEADER_FALLBACK
  const pillsH = document.getElementById('history-pills')?.offsetHeight ?? 0
  return headerH + pillsH + 16
}

function setActiveEra(slug: string): void {
  document.querySelectorAll<HTMLButtonElement>('.history-pill').forEach(pill => {
    pill.classList.toggle('is-active', pill.dataset.era === slug)
  })
  const select = document.querySelector<HTMLSelectElement>('.history-era-select')
  if (select && select.value !== slug) select.value = slug
}

function scrollToEra(slug: string): void {
  const target = document.getElementById('era-' + slug)
  if (!target) return
  const top = target.getBoundingClientRect().top + window.scrollY - getStickyOffset()
  window.scrollTo({ top, behavior: 'smooth' })
}

function bindNav(): void {
  document.querySelectorAll<HTMLButtonElement>('.history-pill').forEach(pill => {
    pill.addEventListener('click', e => {
      e.preventDefault()
      const slug = pill.dataset.era
      if (!slug) return
      setActiveEra(slug)
      scrollToEra(slug)
    })
  })

  document.querySelector<HTMLSelectElement>('.history-era-select')?.addEventListener('change', e => {
    const slug = (e.target as HTMLSelectElement).value
    if (!slug) return
    setActiveEra(slug)
    scrollToEra(slug)
  })
}

function initScrollSpy(): void {
  const anchors = Array.from(document.querySelectorAll<HTMLElement>('[id^="era-"]'))
  if (anchors.length === 0) return

  // Maintain an array of {slug, top} sorted by document position. Recomputed
  // on resize / load. Cheap because there are only a handful of anchors.
  const positions: { slug: string; top: number }[] = []
  const recompute = (): void => {
    positions.length = 0
    anchors.forEach(a => {
      positions.push({ slug: a.id.replace('era-', ''), top: a.getBoundingClientRect().top + window.scrollY })
    })
    positions.sort((a, b) => a.top - b.top)
  }

  recompute()
  window.addEventListener('resize', recompute)
  window.addEventListener('load', recompute)

  let ticking = false
  let activeSlug: string | null = null

  window.addEventListener('scroll', () => {
    if (ticking) return
    ticking = true
    window.requestAnimationFrame(() => {
      ticking = false
      const trigger = window.scrollY + getStickyOffset() + 24
      let current: string | null = null
      for (const p of positions) {
        if (trigger >= p.top) current = p.slug
        else break
      }
      if (current && current !== activeSlug) {
        activeSlug = current
        setActiveEra(current)
      }
    })
  }, { passive: true })
}

function initReveals(): void {
  const items = document.querySelectorAll<HTMLElement>('.timeline-item')
  if (items.length === 0) return

  if (typeof IntersectionObserver === 'undefined') {
    items.forEach(el => el.classList.add('is-revealed'))
    return
  }

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed')
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' })

  items.forEach(el => observer.observe(el))
}

// Toggle `.is-stuck` on the pills bar when it pins to the top, so the
// "slip under the header" padding only kicks in while it's stuck.
//
// We shrink the observer's root from the top by (sticky-top + 1px), so
// the element falls out of full visibility (intersectionRatio < 1) the
// moment it pins. Reading the value from `getComputedStyle` keeps this
// in sync if the SCSS `top` value ever changes.
function initStuckObserver(): void {
  const bar = document.getElementById('history-pills')
  if (!bar) return
  const stuckTop = parseFloat(getComputedStyle(bar).top) || 0
  const obs = new IntersectionObserver(
    ([entry]) => bar.classList.toggle('is-stuck', entry.intersectionRatio < 1),
    { threshold: [1], rootMargin: `-${Math.ceil(stuckTop) + 1}px 0px 0px 0px` }
  )
  obs.observe(bar)
}

function boot(): void {
  if (!document.querySelector('.history-page')) return
  syncHeaderOffset()
  window.addEventListener('resize', syncHeaderOffset)
  window.addEventListener('load', syncHeaderOffset)
  bindNav()
  initScrollSpy()
  initReveals()
  initStuckObserver()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot)
} else {
  boot()
}
